import { Box, Typography, Collapse, Tooltip, IconButton } from '@mui/material';
import { useState } from 'react';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PropTypes from 'prop-types';
import { functionList, groupedFunctions } from './Component/FunctionToolbar';

export default function FunctionsSection() {
  const [mainOpen, setMainOpen] = useState(true);
  const [stringUtilitiesOpen, setStringUtilitiesOpen] = useState(false);
  const [statisticalOpen, setStatisticalOpen] = useState(false);
  const [utilityOpen, setUtilityOpen] = useState(false);
  const [dateTimeOpen, setDateTimeOpen] = useState(false);
  const [numberFormattingOpen, setNumberFormattingOpen] = useState(false);

  return (
    <Box>
      {/* Functions Header */}
      <Box
        onClick={() => setMainOpen(!mainOpen)}
        sx={{
          background: 'linear-gradient(100.009deg, rgb(232, 250, 244) 10.544%, rgba(232, 250, 244, 0.2) 149%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 0.75,
          py: 0.5,
          cursor: 'pointer',
          mb: mainOpen ? 3 : 0
        }}
      >
        <Typography
          sx={{
            fontFamily: 'IBM Plex Sans, sans-serif',
            fontWeight: 500,
            fontSize: '0.9375rem',
            color: '#525252',
          }}
        >
          Functions
        </Typography>
        <KeyboardArrowUpIcon 
          sx={{ 
            fontSize: 18, 
            color: '#525252',
            transform: mainOpen ? 'none' : 'rotate(180deg)',
            transition: 'transform 0.2s'
          }} 
        />
      </Box>

      {/* Functions Content */}
      <Collapse in={mainOpen}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* String Utilities */}
          <FunctionItem 
            label="String Utilities"
            open={stringUtilitiesOpen}
            onToggle={() => setStringUtilitiesOpen(!stringUtilitiesOpen)}
          >
            {stringUtilitiesOpen && (
              <FunctionIconsGrid category="String Utilities" />
            )}
          </FunctionItem>

          {/* Date & Time */}
          <FunctionItem 
            label="Date & Time"
            open={dateTimeOpen}
            onToggle={() => setDateTimeOpen(!dateTimeOpen)}
          >
            {dateTimeOpen && (
              <FunctionIconsGrid category="Date & Time" />
            )}
          </FunctionItem>

          {/* Number Formatting & Parsing */}
          <FunctionItem 
            label="Number Formatting & Parsing"
            open={numberFormattingOpen}
            onToggle={() => setNumberFormattingOpen(!numberFormattingOpen)}
          >
            {numberFormattingOpen && (
              <FunctionIconsGrid category="Number Formatting & Parsing" />
            )}
          </FunctionItem>

          {/* Statistical Functions */}
          <FunctionItem 
            label="Statistical Functions"
            open={statisticalOpen}
            onToggle={() => setStatisticalOpen(!statisticalOpen)}
          >
            {statisticalOpen && (
              <FunctionIconsGrid category="Statistical Functions" />
            )}
          </FunctionItem>

          {/* Utility */}
          <FunctionItem 
            label="Utility"
            open={utilityOpen}
            onToggle={() => setUtilityOpen(!utilityOpen)}
          >
            {utilityOpen && (
              <FunctionIconsGrid category="Utility" />
            )}
          </FunctionItem>
        </Box>
      </Collapse>
    </Box>
  );
}

function FunctionItem({ 
  label, 
  open, 
  onToggle,
  children
}) {
  return (
    <Box>
      <Box
        onClick={onToggle}
        sx={{
          background: 'linear-gradient(100.009deg, rgb(232, 250, 244) 10.544%, rgba(232, 250, 244, 0.2) 149%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 0.75,
          py: 0.5,
          cursor: 'pointer'
        }}
      >
        <Typography
          sx={{
            fontFamily: 'IBM Plex Sans, sans-serif',
            fontWeight: 500,
            fontSize: '0.9375rem',
            color: '#525252',
            width: 118
          }}
        >
          {label}
        </Typography>
        <KeyboardArrowUpIcon 
          sx={{ 
            fontSize: 16, 
            color: '#585858',
            transform: open ? 'none' : 'rotate(180deg)',
            transition: 'transform 0.2s'
          }} 
        />
      </Box>
      {children && (
        <Box sx={{ mt: 1, ml: 1 }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

function FunctionSubItem({ label }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer'
      }}
    >
      <Typography
        sx={{
          fontFamily: 'IBM Plex Sans, sans-serif',
          fontSize: '0.875rem',
          color: '#06110A',
          lineHeight: '1.125rem',
          width: 215
        }}
      >
        {label}
      </Typography>
      <KeyboardArrowUpIcon 
        sx={{ 
          fontSize: 16, 
          color: '#585858',
          transform: 'rotate(180deg)'
        }} 
      />
    </Box>
  );
}

FunctionItem.propTypes = { 
  label: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node
};

// Function Icons Grid Component
function FunctionIconsGrid({ category }) {
  const functions = groupedFunctions[category] || [];
  
  const handleDragStart = (e, func) => {
    const dragData = {
      type: 'functionNode',
      functionType: func.id,
      icon: func.icon,
    };

    // Add config data for currency_format function
    if (func.id === 'currency_format' && func.config) {
      dragData.config = func.config;
    }

    e.dataTransfer.setData(
      'application/reactflow',
      JSON.stringify(dragData)
    );
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 1,
        p: 1
      }}
    >
      {functions.map((funcId) => {
        const func = functionList.find((f) => f.id === funcId);
        if (!func) return null;

        return (
          <Tooltip
            key={func.id}
            title={
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {func.label}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                  {func.description}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block',
                    fontStyle: 'italic',
                    opacity: 0.9
                  }}
                >
                  {func.example}
                </Typography>
              </Box>
            }
            placement="right"
            arrow
          >
            <IconButton
              draggable
              onDragStart={(e) => handleDragStart(e, func)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                backgroundColor: '#EEF2FF',
                border: '1px solid rgba(40, 41, 43, 0.09)',
                cursor: 'grab',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#e5e7eb',
                  borderColor: '#d1d5db',
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  cursor: 'grabbing',
                  transform: 'translateY(0px)',
                },
              }}
            >
              <Box
                component="img"
                src={func.icon}
                alt={func.label}
                sx={{
                  width: 16,
                  height: 16,
                  objectFit: 'contain',
                  filter: 'brightness(0) saturate(100%) invert(20%) sepia(100%) saturate(2000%) hue-rotate(220deg) brightness(95%) contrast(95%)',
                }}
              />
            </IconButton>
          </Tooltip>
        );
      })}
    </Box>
  );
}

FunctionIconsGrid.propTypes = {
  category: PropTypes.string.isRequired
};

FunctionSubItem.propTypes = {
  label: PropTypes.string.isRequired
};
